/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormInput, ImageUpload, FormCheckbox } from '../components';
import PageHeader from '@src/mobile/components/PageHeader';
import SharePanel from '@src/mobile/components/SharePanel';
import { submitComplaint } from '@src/services/api';
import styles from './index.css';
import NavBar from '@src/mobile/components/NavBar';
import errorBoundary from '@ifeng-fe/errorBoundary';

const getListUrl = () => {
    const path = window.location.pathname;
    const base = path
        .replace(/\/detail\/?$/, '')
        .replace(/\/list\/?$/, '')
        .replace(/\/form\/?$/, '')
        .replace(/\/$/, '');
    return `${window.location.origin}${base}/list`;
};

const INITIAL_FORM_DATA = {
    nickname: '',
    phone: '',
    cmp_target: '',
    cmp_issue: '',
    cmp_detail: '',
    images: [],
    image_hidden: 0,
};

const phoneReg = /^1[3-9]\d{9}$/;
const normalizeUploadedUrl = (item) => {
    if (!item) {
        return '';
    }
    if (typeof item === 'string') {
        return item.startsWith('blob:') ? '' : item;
    }
    const url = item.uploadedUrl || item.url || '';
    if (!url || url.startsWith('blob:')) {
        return '';
    }
    return url;
};
const FORM_SHARE_TITLE = '凤凰投诉';
const FORM_SHARE_DESC = '聚焦金融&股市投资者心声，欢迎提交你的投诉与建议。';

const validateForm = (data, setErrors) => {
    const errors = {};
    if (!data.nickname?.trim()) errors.nickname = '请输入姓名';
    else if (data.nickname.length > 20) errors.nickname = '姓名最多20字';
    if (!data.phone?.trim()) errors.phone = '请输入手机号';
    else if (!phoneReg.test(data.phone)) errors.phone = '请输入正确的手机号';
    if (!data.cmp_target?.trim()) errors.cmp_target = '请输入投诉对象';
    else if (data.cmp_target.length > 30) errors.cmp_target = '投诉对象最多30字';
    if (!data.cmp_issue?.trim()) errors.cmp_issue = '请输入诉求问题';
    else if (data.cmp_issue.length > 100) errors.cmp_issue = '诉求问题最多100字';
    if (!data.cmp_detail?.trim()) errors.cmp_detail = '请输入投诉详情';
    else if (data.cmp_detail.length < 20) errors.cmp_detail = '投诉详情至少20字';
    else if (data.cmp_detail.length > 1000) errors.cmp_detail = '投诉详情最多1000字';

    setErrors(errors);
    return Object.keys(errors).length === 0;
};

const Layout = ({ content }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const updateField = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting || isSubmitted) {
            return;
        }
        setSubmitStatus(null);

        if (!validateForm(formData, setErrors)) {
            return;
        }

        setIsSubmitting(true);
        try {
            const images = (formData.images || []).map(normalizeUploadedUrl).filter(Boolean);

            const body = {
                nickname: formData.nickname,
                phone: formData.phone,
                cmp_target: formData.cmp_target,
                cmp_issue: formData.cmp_issue,
                cmp_detail: formData.cmp_detail,
                images,
                image_hidden: formData.image_hidden,
            };

            await submitComplaint(body);
            setSubmitStatus({ success: true, message: '提交成功！感谢您的反馈。' });
            setIsSubmitted(true);

            setTimeout(() => {
                location.href = getListUrl();
            }, 1000);
        } catch (err) {
            setSubmitStatus({
                success: false,
                message: err.message || '提交失败，请稍后重试',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.layout}>
            <SharePanel title={FORM_SHARE_TITLE} desc={FORM_SHARE_DESC} url={window.location.href} />
            <PageHeader desc="聚焦金融&股市投资者心声，等你来吐槽/点赞！" variant="banner" />
            <NavBar />
            <div className={styles.box}>
                <form className={styles.form} onSubmit={onSubmit}>
                    <FormInput label="投诉对象" placeholder="请输入投诉对象" error={errors.cmp_target} value={formData.cmp_target} onChange={(e) => updateField('cmp_target', e.target.value)} />

                    <FormInput label="诉求问题" placeholder="请输入诉求问题" error={errors.cmp_issue} value={formData.cmp_issue} onChange={(e) => updateField('cmp_issue', e.target.value)} />

                    <FormInput
                        label="投诉详情"
                        placeholder="请详细描述投诉内容（20-1000字）"
                        multiline
                        rows={6}
                        maxLength={1000}
                        error={errors.cmp_detail}
                        value={formData.cmp_detail}
                        onChange={(e) => updateField('cmp_detail', e.target.value)}
                    />

                    <ImageUpload files={formData.images} onChange={(files) => updateField('images', files)} error={errors.images} />

                    <FormCheckbox label="图片是否隐藏" checked={formData.image_hidden === 1} onChange={(val) => updateField('image_hidden', val)} name="image_hidden" />

                    <FormInput label="昵称" placeholder="请输入您的昵称" maxLength={20} error={errors.nickname} value={formData.nickname} onChange={(e) => updateField('nickname', e.target.value)} />

                    <FormInput label="手机号" placeholder="请输入手机号" type="tel" error={errors.phone} value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} />

                    {submitStatus && <div className={submitStatus.success ? styles.statusSuccess : styles.statusError}>{submitStatus.message}</div>}

                    <button type="submit" className={styles.submitBtn} disabled={isSubmitting || isSubmitted}>
                        {isSubmitted ? '已提交' : isSubmitting ? '提交中...' : '提交'}
                    </button>
                </form>
            </div>
        </div>
    );
};

Layout.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Layout);
